import { atom } from 'jotai';

/**
 * Bumped each time the user clicks "Scribe" in the sidebar while the session
 * list page is already mounted. The panel controls watch this nonce and force
 * the split view back open, so clicking Scribe always returns to split — even
 * from a collapsed full-screen detail, where navigating to the same session URL
 * is a no-op and would otherwise leave the list collapsed.
 *
 * Lives in its own module (not the context file) so the always-mounted sidebar
 * can import the atom without pulling in the panel-controls hook.
 */
export const requestSessionsSplitAtom = atom(0);

/**
 * Bumped by `useSessionDeletedRecovery` when a delete removes the open session.
 * The list re-opens the first remaining session; the panel drops the id from its
 * `lastViewedSessionId` highlight. `excludeSessionIds` lets both skip the
 * just-deleted ids before the list query refetches.
 */
export const restoreFirstSessionRequestAtom = atom<{
  nonce: number;
  excludeSessionIds: string[];
}>({ nonce: 0, excludeSessionIds: [] });

/** True while the session list handles deleted-session recovery. */
export const sessionListHandlesDeleteRecoveryAtom = atom(false);

/**
 * Source of truth for the sessions page's left-panel collapse state. An atom
 * (not local state in `useSessionsPanelControls`) so callers above the panel
 * context — which only wraps `/scribe/sessions/*` — can read it. Reset to
 * `false` on context unmount so a stale `true` from a prior visit doesn't
 * carry into the next mount.
 */
export const sessionListCollapsedAtom = atom(false);

/**
 * Whether the session list is currently open as a left side sheet. On small
 * breakpoints the sessions page collapses to a single pane, so an open session
 * would otherwise hide the list entirely; this sheet overlays it on demand.
 * In-memory only — a resize must never auto-open it, and the sheet
 * only exists in single-pane detail, so there's nothing to persist. Reset to
 * `false` on context unmount alongside `sessionListCollapsedAtom` so a stale
 * `true` from a prior visit can't spring the sheet open on the next mount.
 */
export const sessionListSheetOpenAtom = atom(false);

/**
 * Mirrors `useSessionsPanelControls`'s `canFitSplit` — published so the
 * sidebar can suppress the "Open session list" tooltip in single-pane mode,
 * and so `ChatMobileModal` can host in-session Evidence whenever the split
 * (and thus `EvidenceSidebarPanel`) is absent (EVI-2119). Mirror (not
 * source-of-truth like `sessionListCollapsedAtom`) because `canFitSplit`
 * only changes on resize, so one-render sync lag is imperceptible and
 * doesn't justify touching the panel context's `useState`. Reset to `true`
 * when the sessions page unmounts so a stale `false` can't keep the drawer
 * mounted on other routes.
 */
export const sessionListCanFitSplitAtom = atom(true);
